import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, TYPE_META, timeAgo } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Plus, LayoutGrid, List as ListIcon, FileBarChart } from "lucide-react";
import { toast } from "sonner";

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isClient = user.role === "client";
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("board");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", type: "request", priority: "medium", client_id: "", project_id: "" });
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const dragRef = useRef(null);

  const onDrop = (s) => {
    const id = dragRef.current;
    if (id) {
      const t = tickets.find((x) => x.id === id);
      if (t && t.status !== s) changeStatus(t, s);
    }
    dragRef.current = null;
    setDragId(null);
    setOverCol(null);
  };

  const load = useCallback(() => api.get("/tickets").then((r) => setTickets(r.data)), []);
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
    if (!isClient) api.get("/clients").then((r) => setClients(r.data));
  }, [load, isClient]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!isClient && !form.client_id) return toast.error("Select a client");
    setSaving(true);
    try {
      await api.post("/tickets", { ...form, client_id: isClient ? undefined : form.client_id, project_id: form.project_id || null });
      toast.success("Ticket created");
      setOpen(false);
      setForm({ title: "", description: "", type: "request", priority: "medium", client_id: "", project_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (t, status) => {
    await api.patch(`/tickets/${t.id}`, { status });
    load();
  };

  const NewTicketDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="new-ticket-btn">
          <Plus size={16} className="mr-1" /> New Ticket
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Create a ticket</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!isClient && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="ticket-title-input" placeholder="What do you need?" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={4} data-testid="ticket-desc-input" placeholder="Add details, links, examples…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Project (optional)</Label>
            <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="ticket-project-select"><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.filter((p) => isClient || !form.client_id || p.client_id === form.client_id).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={saving} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-ticket-btn">
            {saving ? "Creating…" : "Create Ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const TicketCard = ({ t }) => (
    <div data-testid="ticket-card" draggable
      onDragStart={(e) => { dragRef.current = t.id; setDragId(t.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => { dragRef.current = null; setDragId(null); setOverCol(null); }}
      className={`rounded-md border border-slate-200 bg-white p-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-grab active:cursor-grabbing ${dragId === t.id ? "opacity-50" : ""}`}>
      <div onClick={() => navigate(`/tickets/${t.id}`)}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{TYPE_META[t.type]}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_META[t.priority].bg}`}>{PRIORITY_META[t.priority].label}</span>
        </div>
        <p className="text-sm font-medium text-slate-800 leading-snug line-clamp-2">
          {t.number ? <span className="text-slate-400 font-semibold">#{t.number} · </span> : null}{t.title}
        </p>
        <div className="flex items-center justify-between mt-3 text-[11px] text-slate-400">
          <span className="truncate">{t.client_name}</span>
          <span className="flex items-center gap-1"><MessageSquare size={12} /> {t.comment_count}</span>
        </div>
      </div>
      <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
        <SelectTrigger className="mt-2 h-7 text-xs" data-testid="card-status-select"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Tickets</h1>
          <p className="text-slate-500 text-sm mt-1">{tickets.length} total · live collaboration board</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView("board")} data-testid="view-board-btn"
              className={`h-8 w-8 rounded flex items-center justify-center ${view === "board" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid size={15} /></button>
            <button onClick={() => setView("list")} data-testid="view-list-btn"
              className={`h-8 w-8 rounded flex items-center justify-center ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500"}`}><ListIcon size={15} /></button>
          </div>
          <Button variant="outline" onClick={() => navigate("/reports")} data-testid="generate-report-nav-btn">
            <FileBarChart size={16} className="mr-1.5" /> Generate Report
          </Button>
          {NewTicketDialog}
        </div>
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 items-start">
          {STATUS_ORDER.map((s) => {
            const col = tickets.filter((t) => t.status === s);
            return (
              <div key={s}
                onDragOver={(e) => { e.preventDefault(); setOverCol(s); }}
                onDragLeave={() => setOverCol((c) => (c === s ? null : c))}
                onDrop={() => onDrop(s)}
                className={`rounded-md border p-2.5 transition-colors ${overCol === s ? "bg-orange-50 border-orange-300" : "bg-slate-100/60 border-slate-200"}`}
                data-testid={`column-${s}`}>
                <div className="flex items-center gap-2 px-1 pb-2.5 sticky top-0">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_META[s].dot }} />
                  <span className="text-sm font-medium text-slate-700">{STATUS_META[s].label}</span>
                  <span className="ml-auto text-xs text-slate-400">{col.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[40px]">
                  {col.map((t) => <TicketCard key={t.id} t={t} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-slate-200 divide-y">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} data-testid="ticket-row"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: STATUS_META[t.status].dot }} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {t.number ? <span className="text-slate-400 font-semibold">#{t.number} · </span> : null}{t.title}
                </p>
                <p className="text-[11px] text-slate-400">{t.client_name} · {TYPE_META[t.type]} · {timeAgo(t.updated_at)}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded ${PRIORITY_META[t.priority].bg}`}>{PRIORITY_META[t.priority].label}</span>
              <span className="text-[11px] text-slate-400 flex items-center gap-1 w-10 justify-end"><MessageSquare size={12} />{t.comment_count}</span>
            </button>
          ))}
          {tickets.length === 0 && <p className="text-center text-sm text-slate-400 py-10">No tickets yet.</p>}
        </Card>
      )}
    </div>
  );
}
